import Box from '@mui/material/Box';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { ChangeEvent, useCallback, useState } from 'react';
import { FormFieldValue } from '../forms/FormFieldValue';
import NumberField from '../NumberField';
import { type ConsumableLineItem, ConsumableLineItemUseType, validate as validateConsumableLineItem } from '../../models/ConsumableLineItem';
import { MenuItem } from '@mui/material';

export const newConsumableLineItem = {
  id: 0,
  description: ''
} as ConsumableLineItem;

export interface ConsumableLineItemFormState {
  values: Partial<Omit<ConsumableLineItem, 'id'>>;
  errors: Partial<Record<keyof ConsumableLineItemFormState['values'], string>>;
}

export interface ConsumableLineItemFormProps {
  initialValue: ConsumableLineItem;
  initialErrors?: Partial<Record<keyof ConsumableLineItemFormState['values'], string>>;
  onReset?: (formValues: Partial<ConsumableLineItemFormState['values']>) => void;
  onFormValuesUpdate?: (formValues: Partial<ConsumableLineItemFormState['values']>) => void;
}

export default function ConsumableLineItemForm(props: ConsumableLineItemFormProps) {
  const {
    initialValue,
    initialErrors = {},
    onReset = () => { },
    onFormValuesUpdate = () => { }
  } = props;

  const [errors, setErrors] = useState<Partial<Record<keyof ConsumableLineItemFormState['values'], string>>>({});

  const handleFormFieldChange = useCallback(
    (name: keyof ConsumableLineItemFormState['values'], value: FormFieldValue) => {
      const validateField = (values: Partial<ConsumableLineItemFormState['values']>) => {
        const { issues } = validateConsumableLineItem(values);

        const issue = issues?.find((issue) => issue.path?.[0] === name);

        if (issue) {
          setErrors({
            ...errors,
            [name]: issue.message,
          });
          return false;
        }

        return true;
      };

      const newFormValues = { ...initialValue, [name]: value };

      const valid = validateField(newFormValues);

      if (valid) {
        onFormValuesUpdate(newFormValues);
      }
    },
    [initialValue, errors, onFormValuesUpdate],
  );

  const handleNumberFieldChange = useCallback(
    (value: number | null, fieldName: string) => {
      handleFormFieldChange(
        fieldName as keyof ConsumableLineItemFormState['values'],
        value,
      );
    },
    [handleFormFieldChange],
  );

  const handleTextFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      handleFormFieldChange(
        event.target.name as keyof ConsumableLineItemFormState['values'],
        event.target.value,
      );
    },
    [handleFormFieldChange],
  );

  const handleReset = useCallback(() => {
    if (onReset) {
      onReset(initialValue || newConsumableLineItem);
    }
  }, [initialValue, onReset]);

  return (
    <Box
      onReset={handleReset}
      sx={{ width: '100%' }}
    >
      <FormGroup>
        <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
          <Grid size={{ xs: 6, sm: 3 }} sx={{ display: 'flex' }}>
            <TextField
              required
              value={initialValue.description || ''}
              onChange={handleTextFieldChange}
              slotProps={{ inputLabel: { shrink: true } }}
              name="description"
              label="Description"
              error={!!errors.description || !!initialErrors.description}
              helperText={(errors.description || initialErrors.description) ?? ''}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }} sx={{ display: 'flex' }}>
            <TextField
              select
              value={String(initialValue.consumableLineItemUseType ?? '0')}
              onChange={handleTextFieldChange}
              label="Unit"
              name="consumableLineItemUseType"
              error={!!errors.consumableLineItemUseType}
              fullWidth
              required
            >
              <MenuItem value={ConsumableLineItemUseType.YEARLY}>Yearly</MenuItem>
              <MenuItem value={ConsumableLineItemUseType.BATCH}>Batch</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }} sx={{ display: 'flex' }}>
            <NumberField
              required
              value={initialValue.orderQuantity || null}
              onValueChange={(event) => handleNumberFieldChange(event, 'orderQuantity')}
              name="orderQuantity"
              label="Quantity"
              error={!!errors.orderQuantity || !!initialErrors.orderQuantity}
              helperText={(errors.orderQuantity || initialErrors.orderQuantity) ?? ''}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }} sx={{ display: 'flex' }}>
            <NumberField
              required
              value={initialValue.unitQuantity || null}
              onValueChange={(event) => handleNumberFieldChange(event, 'unitQuantity')}
              name="unitQuantity"
              label="Unit Quantity"
              error={!!errors.unitQuantity || !!initialErrors.unitQuantity}
              helperText={(errors.unitQuantity || initialErrors.unitQuantity) ?? ''}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }} sx={{ display: 'flex' }}>
            <NumberField
              required
              value={initialValue.unitCost || null}
              onValueChange={(event) => handleNumberFieldChange(event, 'unitCost')}
              name="unitCost"
              label="Unit Cost"
              error={!!errors.unitCost || !!initialErrors.unitCost}
              helperText={(errors.unitCost || initialErrors.unitCost) ?? ''}
              fullWidth
              currencyField
            />
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }} sx={{ display: 'flex' }}>
            <NumberField
              value={initialValue.shippingCost || null}
              onValueChange={(event) => handleNumberFieldChange(event, 'shippingCost')}
              name="shippingCost"
              label="Shipping Cost"
              fullWidth
              currencyField
            />
          </Grid>
          {initialValue.consumableLineItemUseType === ConsumableLineItemUseType.BATCH && (
            <Grid size={{ xs: 4, sm: 2 }} sx={{ display: 'flex' }}>
              <NumberField
                value={initialValue.batchQuantity || null}
                onValueChange={(event) => handleNumberFieldChange(event, 'batchQuantity')}
                name="batchQuantity"
                label="Batch Quantity"
                fullWidth
                currencyField
              />
            </Grid>
          )}
        </Grid>
      </FormGroup>
    </Box>
  );
}
